import { useMemo } from 'react';
import { useAuthContext } from "./AuthContext";
import { useTenant } from "./useTenant";

export const usePermissions = () => {
    const { user, acl, vistas } = useAuthContext();
    const { id: tenantId, features: tFeatures, system_features: sFeatures } = useTenant();

    // Falla CERRADO: sin lista, no se concede nada. El modelo anterior (hasPermission)
    // devolvia true cuando la clave no estaba en el ACL y no habia tenant, asi que un
    // permiso mal escrito concedia acceso en vez de negarlo.
    const concedidas = useMemo(() => new Set(vistas ?? []), [vistas]);

    const puedeVer = (clave) => concedidas.has(clave);

    const featureKeySet = useMemo(() => {
        const normalize = (items) => {
            if (!items) return [];
            return (Array.isArray(items) ? items : [items])
                .map(i => typeof i === 'string' ? i : (i?.feature_key || i?.key || i?.code || i?.slug || i?.name))
                .filter(Boolean);
        };

        return new Set([
            ...normalize(tFeatures),
            ...normalize(sFeatures),
            ...normalize(user?.features),
            ...normalize(user?.permissions)
        ]);
    }, [tFeatures, sFeatures, user]);

    /**
     * @deprecated Falla ABIERTO: cuando la clave no esta en `acl` y no hay
     * tenant, devuelve `true` (ver paso 3). No usar en codigo nuevo.
     * `puedeVer` es el reemplazo (falla CERRADO). Se mantiene solo por el
     * unico uso que queda en App.jsx (ruta /testing).
     */
    const hasPermission = (feature, permissionKey) => {
        // 1. Verificamos el ACL del usuario (siempre manda sobre el resto)
        if (acl && permissionKey && permissionKey in acl) {
            return acl[permissionKey] === true;
        }
        
        // 2. Si hay tenant, verificamos que tenga la feature contratada
        // Si no tiene la feature ni el permiso específico en el set, denegamos.
        if (tenantId && !featureKeySet.has(feature) && !featureKeySet.has(permissionKey)) {
            return false;
        }
        
        // 3. Si no hay regla específica en ACL pero sí en features (o no hay tenant), se permite
        return true; 
    };

    const isAdmin = user?.role?.includes('ADMIN') || user?.role === 'SUPER_ADMIN';
    const isSuperAdmin = ['SUPER_ADMIN', 'SUPERADMIN'].includes(user?.role) || user?.role_key === 'super_admin';

    return { hasPermission, puedeVer, isAdmin, isSuperAdmin, user };
};